import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { ItemImageButton, ItemThumb } from "@/components/ItemImage";

const EMPTY = { sku: "", name: "", description: "", category: "", unit: "", unit_price: 0, cost_price: 0, stock: 0, has_gst: true };
const FIELDS = [["sku", "SKU *"], ["name", "Name *"], ["category", "Category"], ["unit", "Unit (EA, KG…)"], ["unit_price", "Sell price ex GST", "number"], ["cost_price", "Cost price ex GST", "number"], ["stock", "Stock count", "number"]];

export const ItemDialog = ({ open, onOpenChange, item, onSaved }) => {
  const [f, setF] = useState(EMPTY);
  useEffect(() => { if (open) setF(item ? { ...EMPTY, ...item } : EMPTY); }, [open, item]);

  const submit = async (e) => {
    e.preventDefault();
    const body = { ...Object.fromEntries(Object.keys(EMPTY).map((k) => [k, f[k]])), unit_price: Number(f.unit_price), cost_price: Number(f.cost_price || 0), stock: Number(f.stock) };
    try {
      item ? await api.put(`/items/${item.id}`, body) : await api.post("/items", body);
      toast.success("Item saved");
      onOpenChange(false);
      onSaved();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" aria-describedby={undefined} data-testid="item-dialog">
        <DialogHeader><DialogTitle>{item ? "Edit item" : "New item"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="grid gap-4 sm:grid-cols-2">
          {FIELDS.map(([k, l, type]) => (
            <div key={k}><Label>{l}</Label><Input type={type || "text"} step="any" required={k === "sku" || k === "name"} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="mt-1.5" data-testid={`item-${k}-input`} /></div>
          ))}
          <div className="sm:col-span-2"><Label>Description</Label><Input value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className="mt-1.5" data-testid="item-description-input" /></div>
          {item && (
            <div className="flex items-center gap-4 sm:col-span-2">
              <ItemThumb item={item} className="h-20 w-20 rounded border border-slate-200" />
              <ItemImageButton item={item} variant="outline" label={item.image_path ? "Replace image" : "Upload image"} onUploaded={() => { onOpenChange(false); onSaved(); }} />
            </div>
          )}
          <div className="flex items-center gap-3 sm:col-span-2">
            <Switch checked={f.has_gst} onCheckedChange={(v) => setF({ ...f, has_gst: v })} data-testid="item-gst-switch" />
            <Label>{f.has_gst ? "GST applies (10%)" : "GST free"}</Label>
          </div>
          <DialogFooter className="sm:col-span-2"><Button type="submit" className="bg-[#01195D] hover:bg-[#0A2A7A]" data-testid="item-save-btn">Save item</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
